import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {Observable, of, switchMap} from 'rxjs';
import { FrontStationHttpService } from '../../services/http/stationHttp.service';
import { IziviaLoggerService } from '../../services/common/iziviaLogger.service';
import { StationDto } from '../../domain/infra/station/station';
import { UserAction } from '../../domain/infra/userAction';
import { CONTEXT_KEY_PARAM } from '../../services/business/newChargeContext.service';
import { NewChargeContextService } from '../../services/business/newChargeContext.service';
import {STATION_PATH_PARAM} from './chargeAreas.routes';
import {catchError, tap} from 'rxjs/operators';
import {PaynowStationContextService} from '../../services/business/paynowStationContext.service';
import {StationUtilsService} from '../../services/utils/stationUtils.service';

@Injectable()
export class StationResolver implements Resolve<StationDto | null> {
    constructor(
        private readonly stationService: FrontStationHttpService,
        private readonly newChargeContextService: NewChargeContextService,
        private readonly loggerService: IziviaLoggerService,
        private readonly paynowStationContextService: PaynowStationContextService,
        private readonly stationUtilsService: StationUtilsService) {
    }

    resolve(route: ActivatedRouteSnapshot, _: RouterStateSnapshot): Observable<StationDto | null> {
        const stationId = route.paramMap.get(STATION_PATH_PARAM);
        const contextId = route.queryParamMap.get(CONTEXT_KEY_PARAM);
        let chargeContext = this.newChargeContextService.getNewChargeContext(contextId);

        if (!chargeContext) {
            chargeContext =
                this.newChargeContextService.getNewChargeContext();
        }

        if (chargeContext && !chargeContext.stationId) {
            chargeContext.stationId = stationId;
        }

        return this.paynowStationContextService.getStation(contextId)
            .pipe(
                switchMap(station => {
                    if (!station) {
                        return this.stationService.findStation(
                            stationId,
                            this.stationUtilsService.newChargeStationLazyCategories
                        ).pipe(
                            tap(stationFound =>
                                this.paynowStationContextService.set({contextId, station: stationFound})
                            )
                        );
                    } else {
                        return of(station);
                    }
                }),
                tap(station => {
                    chargeContext.isReservable = station.currentUserActions.includes(UserAction.RESERVE);
                }),
                catchError(err => {
                    this.loggerService.debug('Could not resolve station...', err);

                    // Catch error and turns it into a valid null value to avoid blank screen: all depending view(s) should handle the null case
                    // to improve user experience (by redirecting to another route for instance).
                    return of(null);
                })
            );
    }
}
